import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Sparkles, TrendingUp, Camera, Palette } from "lucide-react";
import api, { assetImage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { AssetCard } from "@/components/AssetCard";

export default function Home() {
  const { t } = useI18n();
  const { openAuth, user } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/assets", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.assets));
    api.get("/assets", { params: { sort: "popular", limit: 8 } }).then((r) => setTrending(r.data.assets));
  }, []);

  const heroImages = featured.slice(0, 4);

  const categories = [
    { name: "Architecture", icon: Camera, color: "from-blue-500 to-indigo-600" },
    { name: "3D Graphic", icon: Palette, color: "from-fuchsia-500 to-pink-600" },
    { name: "Landscape", icon: Camera, color: "from-emerald-500 to-teal-600" },
    { name: "Illustration", icon: Palette, color: "from-amber-500 to-orange-600" },
  ];

  const submit = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-40">
          <div className="grid h-full grid-cols-2 md:grid-cols-4">
            {heroImages.map((img, i) => (
              <div key={i} className="relative overflow-hidden">
                <img src={assetImage(img)} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/85 to-slate-900/60" />

        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold backdrop-blur">
              <Sparkles size={14} className="text-amber-400" /> {t("hero.badge")}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              {t("hero.title1")}
              <br />
              <span className="text-blue-500">{t("hero.title2")}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">{t("hero.subtitle")}</p>

            <form onSubmit={submit} className="mt-8 flex max-w-xl overflow-hidden rounded-2xl bg-white p-1.5 shadow-2xl">
              <div className="flex flex-1 items-center gap-2 px-4">
                <Search size={20} className="text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("nav.searchPlaceholder")}
                  className="w-full bg-transparent py-3 text-slate-900 outline-none placeholder:text-slate-400"
                  data-testid="hero-search-input"
                />
              </div>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition"
                data-testid="hero-search-btn"
              >
                {t("hero.searchBtn")} <ArrowRight size={16} />
              </button>
            </form>

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/browse")}
                className="rounded-xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
                data-testid="hero-browse-btn"
              >
                {t("hero.browseAll")}
              </button>
              {(!user || user.role === "buyer") && (
                <button
                  onClick={() => (user ? navigate("/studio") : openAuth("register"))}
                  className="rounded-xl border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
                  data-testid="hero-contributor-btn"
                >
                  {t("hero.becomeContributor")}
                </button>
              )}
            </div>

            <div className="mt-12 flex gap-10">
              {[
                { n: "1.2M+", l: t("hero.stat1") },
                { n: "45K+", l: t("hero.stat2") },
                { n: "8.5M+", l: t("hero.stat3") },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-2xl font-extrabold sm:text-3xl">{s.n}</div>
                  <div className="mt-0.5 text-xs uppercase tracking-widest text-slate-400">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {t("section.categories")}
        </h2>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {categories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <motion.button
                key={cat.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                onClick={() => navigate(`/browse?category=${encodeURIComponent(cat.name)}`)}
                className={`group relative flex h-32 flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br ${cat.color} p-5 text-left text-white`}
                data-testid={`category-card-${cat.name}`}
              >
                <Icon size={24} className="opacity-90" />
                <span className="font-display text-lg font-bold">{cat.name}</span>
                <ArrowRight size={18} className="absolute bottom-5 right-5 opacity-0 transition group-hover:opacity-100" />
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              <Sparkles size={22} className="text-amber-500" /> {t("section.featured")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("section.featuredSub")}</p>
          </div>
          <button
            onClick={() => navigate("/browse")}
            className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:gap-2.5 transition-all"
            data-testid="featured-viewall"
          >
            {t("section.viewAll")} <ArrowRight size={15} />
          </button>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {featured.map((a, i) => (
            <AssetCard key={a.id} asset={a} index={i} />
          ))}
        </div>
      </section>

      {/* Trending */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="flex items-end justify-between">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            <TrendingUp size={22} className="text-blue-600" /> {t("section.trending")}
          </h2>
          <button
            onClick={() => navigate("/browse?sort=popular")}
            className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:gap-2.5 transition-all"
            data-testid="trending-viewall"
          >
            {t("section.viewAll")} <ArrowRight size={15} />
          </button>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {trending.map((a, i) => (
            <AssetCard key={a.id} asset={a} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
